export type SamplePrompt = {
  n: number;
  title: string;
  text: string;
  purpose: string;
  tips: string[];
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
];

export const demoPromptExcerpt =
  'Act as a professional documentation assistant for private security operations. Help me organize the verified information I provide into a clear, objective, chronological incident report. Use only the facts supplied in the source information. Do not invent events, names, times, observations, statements, evidence, or actions…';

export const demoOutput = {
  title: 'SECURITY INCIDENT REPORT — DRAFT',
  sections: [
    {
      heading: 'Incident Details',
      lines: ['Date and time: [Provided date and time]', 'Location: [Provided location]', 'Reporting personnel: [Provided name or role]'],
    },
    { heading: 'Initial Observation', lines: ['Objective summary based only on the information supplied.'] },
    {
      heading: 'Chronological Description of Events',
      lines: ['Events organized in chronological order, preserving the source information and identifying any gaps.'],
    },
    { heading: 'Actions Taken', lines: ['Actions documented in the source information, without adding unsupported details.'] },
    {
      heading: 'Information Requiring Verification',
      lines: ['Missing date or time, if applicable', 'Unconfirmed detail, if applicable'],
    },
  ],
};

export const reviewSteps = [
  'Confirm that the chronology matches your original records.',
  'Verify all dates, times, names, and locations.',
  'Check that the report does not contain invented or unsupported details.',
  "Follow your employer's documentation policies.",
  'Remove or protect sensitive information as required.',
  'Obtain any required supervisor review before submission.',
];

export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'Daily Activity Report',
    text: `Act as a professional documentation assistant for private security operations. Help me prepare a clear and organized daily activity report using only the information I provide.

Organize the report into the following sections:
- Reporting Date and Shift
- Site or Location
- Routine Activities Performed
- Patrols and Inspections Documented
- Significant Observations
- Incidents or Exceptions, If Any
- Notifications and Follow-Up
- Outstanding Tasks

Use objective, professional language and maintain the original meaning of the information.

Do not invent patrols, inspections, incidents, timestamps, or actions. If information is missing, identify the relevant field for completion.

Source information:
[DATE]
[SHIFT]
[SITE OR LOCATION]
[ACTIVITIES ACTUALLY PERFORMED]
[OBSERVATIONS]
[INCIDENTS OR EXCEPTIONS, IF ANY]
[NOTIFICATIONS MADE]
[OUTSTANDING TASKS]

Prepare the report as a draft for review by the responsible security professional.`,
    purpose: 'Create a consistent starting structure for routine daily reporting.',
    tips: ['Check every activity and time against your patrol log before submitting.'],
  },
  {
    n: 2,
    title: 'Shift Handover Summary',
    text: `Act as an operational documentation assistant for private security shift handovers.

Organize the supplied information into a concise, professional handover summary for the incoming authorized team.

Use these sections:
- Outgoing Shift Details
- Incoming Shift Details, If Provided
- Activities Completed
- Outstanding Tasks
- Incidents Requiring Follow-Up
- Equipment or Access Items Requiring Attention
- Relevant Site Observations
- Notifications or Instructions Received
- Items Requiring Confirmation

Use only the source information. Clearly distinguish completed actions from pending tasks. Do not imply that a task was completed unless the source confirms it.

If essential details are missing, identify them without guessing.

Source information:
[DATE AND SHIFT]
[SITE]
[COMPLETED ACTIVITIES]
[PENDING TASKS]
[INCIDENTS REQUIRING FOLLOW-UP]
[EQUIPMENT OR ACCESS ITEMS]
[RELEVANT OBSERVATIONS]
[NOTIFICATIONS OR INSTRUCTIONS]

Prepare a structured handover draft for review by the authorized personnel.`,
    purpose: 'Help organize information that needs to be communicated between shifts.',
    tips: ['Confirm keys, equipment, and pending items in person with the incoming officer.'],
  },
  {
    n: 3,
    title: 'Client Incident Notification',
    text: `Act as a professional business communication assistant for a private security service provider.

Draft a clear, courteous, factual client notification based only on the verified information supplied.

The message should include:
- A concise subject line
- The date, time, and location, if authorized for disclosure
- A neutral summary of the documented incident
- Actions actually taken
- Notifications or follow-up already completed
- Any outstanding items requiring further confirmation
- An appropriate professional closing

Do not speculate about responsibility, make unsupported assurances, disclose unnecessary personal information, or promise outcomes that have not been confirmed.

Adapt the tone to professional client communication and flag any missing information before finalization.

Source information:
[CLIENT OR AUTHORIZED RECIPIENT]
[INCIDENT DATE AND TIME]
[LOCATION]
[VERIFIED INCIDENT SUMMARY]
[ACTIONS ACTUALLY TAKEN]
[CONFIRMED FOLLOW-UP]
[AUTHORIZED CONTACT DETAILS]

Prepare a draft for review and approval before sending.`,
    purpose: 'Create a structured starting point for client communications while preserving accuracy and confidentiality.',
    tips: [
      'Get approval from your supervisor before sending anything to a client.',
      'Remove personal details that the client is not authorized to receive.',
    ],
  },
];
